import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';
import { useAuth } from '../lib/auth';
import { useSidebar } from '../context/SidebarContext';
import { useQueryClient } from '@tanstack/react-query';
import { UserType } from '../lib/auth/types';
import { toast } from 'sonner';
import TourTriggerButton from './tour/TourTriggerButton';



// Import the shared Paddle type declarations from types/index.ts
import { PaddleEventData } from '../types';

// Paddle event handler function
function handlePaddleEvents(
    event: PaddleEventData,
    queryClient: ReturnType<typeof useQueryClient>,
    refreshUserProfile: () => void
) {
    console.log('[Paddle Event]', event.name, event.data);

    switch (event.name) {
        case 'checkout.loaded':
            console.log('[Layout.tsx] [Paddle Event] Checkout loaded.');
            // Potentially show a global loading indicator or log
            break;
        case 'checkout.completed':
            console.log('[Layout.tsx] [Paddle Event] Successful payment/subscription event:', event.name, event.data);

            if (refreshUserProfile) {
                refreshUserProfile();
            }

            // --- START OF CORRECTED Dispatch Custom Event SECTION ---
            let transactionIdForCustomEvent: string | undefined;

            if (event.data && typeof event.data === 'object') {
                // Primary check: transaction_id directly on event.data
                if (typeof (event.data as any).transaction_id === 'string') {
                    transactionIdForCustomEvent = (event.data as any).transaction_id;
                }
                // Fallback 1: nested under event.data.checkout.transaction_id
                else if ((event.data as any).checkout && typeof (event.data as any).checkout.transaction_id === 'string') {
                    transactionIdForCustomEvent = (event.data as any).checkout.transaction_id;
                }
                // Fallback 2: nested under event.data.transaction.id
                else if ((event.data as any).transaction && typeof (event.data as any).transaction.id === 'string') {
                    transactionIdForCustomEvent = (event.data as any).transaction.id;
                }
            }

            if (transactionIdForCustomEvent) {
                const paddleCheckoutCompletedEvent = new CustomEvent('paddleCheckoutActionCompleted', {
                    detail: {
                        transactionId: transactionIdForCustomEvent,
                        paddleEventData: event.data
                    }
                });
                window.dispatchEvent(paddleCheckoutCompletedEvent);
                console.log('[Layout.tsx] Dispatched custom "paddleCheckoutActionCompleted" event with transactionId:', transactionIdForCustomEvent);
            } else {
                console.warn('[Layout.tsx] checkout.completed event received, but no transaction_id found using expected paths in event.data. Custom event not dispatched.', event.data);
            }
            // --- END OF CORRECTED Dispatch Custom Event SECTION ---

            console.log('[Layout.tsx] Successfully processed post-checkout.completed logic (user refresh).');

            console.log('[Layout.tsx] Attempting to close Paddle checkout from global handler (checkout.completed).');
            if (window.Paddle && window.Paddle.Checkout && typeof window.Paddle.Checkout.close === 'function') {
                window.Paddle.Checkout.close();
                console.log('[Layout.tsx] Paddle.Checkout.close() called successfully from global handler.');
            } else {
                console.warn('[Layout.tsx] Paddle.Checkout.close() not called from global handler because Paddle or Checkout object/method is not available.');
            }
            break;
        case 'checkout.closed':
            console.log('[Layout.tsx] [Paddle Event] Checkout closed by user or programmatically.');
            // This event will fire when Paddle.Checkout.close() is called by the local handler too.
            // You might want to ensure data consistency here if a checkout was abandoned.
            // For example, re-fetch subscriptions if the user might have closed it mid-flow.
            // queryClient.invalidateQueries({ queryKey: ['userSubscriptionsList'] }); // Example
            break;
        // ... other cases like checkout.error if you want global handling for them
        default:
            console.log('[Layout.tsx] [Paddle Event] Unhandled event:', event.name);
            break;
    }
}

export default function Layout() {
    const { isSidebarOpen } = useSidebar();
    const { refreshUser, user } = useAuth();
    const queryClient = useQueryClient();
    const location = useLocation();
    const paddleInitializedRef = useRef(false);

    const isAgentPage = location.pathname.startsWith('/agent');




    useEffect(() => {
        if (paddleInitializedRef.current) {
            console.log('[Layout] Paddle already initialized, skipping setup.');
            return;
        }

        const paddleEnv = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';
        const paddleClientKey = import.meta.env.VITE_PADDLE_CLIENT_KEY;

        if (window.Paddle && paddleClientKey) {
            try {
                console.log('[Layout] Attempting Paddle initialization with eventCallback...');
                // Only set environment for sandbox - production is the default
                if (paddleEnv === 'sandbox') {
                    window.Paddle.Environment.set('sandbox');
                }
                // For production, don't call Environment.set() - Paddle defaults to production


                // Define the event handler inline to avoid recreation
                const eventCallbackHandler = (eventData: PaddleEventData) => {
                    console.log('[Paddle Event]', eventData.name, eventData.data);

                    switch (eventData.name) {
                        case 'checkout.loaded':
                            console.log('[Layout.tsx] [Paddle Event] Checkout loaded.');
                            break;
                        case 'checkout.completed':
                            console.log('[Layout.tsx] [Paddle Event] Successful payment/subscription event:', eventData.name, eventData.data);

                            // Immediate refresh attempt
                            if (refreshUser) {
                                refreshUser();
                            }

                            // Directly invalidate subscription queries to ensure UI updates
                            // This is a fallback in case the custom event matching fails
                            // Also re-call refreshUser after delay to catch webhook updates for credits/user_type
                            setTimeout(() => {
                                console.log('[Layout.tsx] Invalidating subscription queries after checkout.completed');
                                queryClient.invalidateQueries({ queryKey: ['userSubscriptionsList'] });
                                queryClient.invalidateQueries({ queryKey: ['userSubscriptionDetail'] });
                                queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
                                // Re-fetch user profile to get updated credits and user_type after webhook processed
                                if (refreshUser) {
                                    console.log('[Layout.tsx] Re-fetching user profile after delay for credits/user_type update');
                                    refreshUser();
                                }
                            }, 2000); // 2 second delay to allow webhook processing

                            // Handle transaction ID extraction for custom events
                            let transactionIdForCustomEvent: string | undefined;

                            if (eventData.data && typeof eventData.data === 'object') {
                                if (typeof (eventData.data as any).transaction_id === 'string') {
                                    transactionIdForCustomEvent = (eventData.data as any).transaction_id;
                                }
                                else if ((eventData.data as any).checkout && typeof (eventData.data as any).checkout.transaction_id === 'string') {
                                    transactionIdForCustomEvent = (eventData.data as any).checkout.transaction_id;
                                }
                                else if ((eventData.data as any).transaction && typeof (eventData.data as any).transaction.id === 'string') {
                                    transactionIdForCustomEvent = (eventData.data as any).transaction.id;
                                }
                            }

                            if (transactionIdForCustomEvent) {
                                const paddleCheckoutCompletedEvent = new CustomEvent('paddleCheckoutActionCompleted', {
                                    detail: {
                                        transactionId: transactionIdForCustomEvent,
                                        paddleEventData: eventData.data
                                    }
                                });
                                window.dispatchEvent(paddleCheckoutCompletedEvent);
                                console.log('[Layout.tsx] Dispatched custom "paddleCheckoutActionCompleted" event with transactionId:', transactionIdForCustomEvent);
                            } else {
                                console.warn('[Layout.tsx] checkout.completed event received, but no transaction_id found using expected paths in eventData.data. Custom event not dispatched.', eventData.data);
                            }

                            console.log('[Layout.tsx] Successfully processed post-checkout.completed logic (user refresh).');

                            console.log('[Layout.tsx] Attempting to close Paddle checkout from global handler (checkout.completed).');
                            if (window.Paddle && window.Paddle.Checkout && typeof window.Paddle.Checkout.close === 'function') {
                                window.Paddle.Checkout.close();
                                console.log('[Layout.tsx] Paddle.Checkout.close() called successfully from global handler.');
                            } else {
                                console.warn('[Layout.tsx] Paddle.Checkout.close() not called from global handler because Paddle or Checkout object/method is not available.');
                            }
                            break;
                        case 'checkout.closed':
                            console.log('[Layout.tsx] [Paddle Event] Checkout closed by user or programmatically.');
                            break;
                        default:
                            console.log('[Layout.tsx] [Paddle Event] Unhandled event:', eventData.name);
                            break;
                    }
                };
                // window.Paddle.globalEventHandler = globalHandler; // REMOVED

                window.Paddle.Initialize({
                    token: paddleClientKey,
                    eventCallback: eventCallbackHandler, // Pass the handler directly
                });
                console.log('[Layout] Paddle Initialized Successfully with eventCallback:', paddleEnv);
                paddleInitializedRef.current = true;
            } catch (error) {
                console.error('[Layout] Error during Paddle initialization:', error);
                toast.error('Could not initialize payment system.');
            }
        }
    }, [refreshUser, queryClient]); // Dependencies for Paddle setup

    return (
        <div className="flex h-screen bg-background text-foreground">
            {/* Sidebar */}
            <div
                className={`
          top-0 left-0 z-30 h-full w-full md:static md:h-full md:w-64
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
                style={{
                    maxWidth: isSidebarOpen ? '100vw' : undefined,
                    position: isSidebarOpen ? 'fixed' : 'fixed',
                }}
            >
                <Sidebar />
            </div>

            {/* Main content */}
            <div
                className={`flex flex-1 flex-col transition-all duration-300 min-w-0
          ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}
        `}
            >
                <MobileHeader />
                <main className={`flex-1 ${isAgentPage ? 'overflow-hidden' : 'overflow-y-scroll'} overflow-x-hidden max-w-full min-w-0 no-scrollbar`}>
                    <Outlet />
                </main>
            </div>

            {/* Tour Trigger Button */}
            <TourTriggerButton />
        </div>
    );
}